/**
 * This browser as an enrolled tablet (#80, D6).
 *
 * Three things are kept here, each where it should live:
 *
 *   - **The tablet's key**, in localStorage: it is the device's credential
 *     and should survive the browser closing. Removing it un-enrols the
 *     tablet as far as this browser is concerned; revoking it on screen 12
 *     is what stops it working everywhere.
 *   - **The unlock**, in sessionStorage: who is holding the tablet, until it
 *     locks. A reload keeps it; closing the browser drops it.
 *   - **A full sign-in for one fenced action** (step-up), in memory only and
 *     for five minutes: a password typed on a shared tablet should not
 *     outlive the thing it was typed for.
 *
 * And one hook: in tablet mode every API response is looked at once, so a
 * request refused because the tablet locked itself, or because an action
 * needs a password, reaches the app wherever in the screens it was made.
 */

import { STEP_UP_HEADER } from "@baros/core";

const KEY = "baros.tablet";
const SESSION = "baros.tabletSession";

/** What this browser holds as an enrolled tablet. */
export interface TabletKey {
  readonly deviceId: string;
  /** `<deviceId>.<secret>` */
  readonly token: string;
  readonly orgId: string;
  readonly venueId: string;
  readonly name: string;
}

/** Who has unlocked it. */
export interface TabletSession {
  /** `<sessionId>.<secret>` */
  readonly token: string;
  readonly userId: string;
  readonly name: string;
  readonly idleMinutes: number | null;
  readonly expiresAt: string;
}

function read<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: unknown): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the tablet simply forgets, and asks again.
  }
}

export const tabletKey = (): TabletKey | null => read<TabletKey>(() => window.localStorage, KEY);
export const setTabletKey = (key: TabletKey | null): void => write(() => window.localStorage, KEY, key);
export const tabletSession = (): TabletSession | null => read<TabletSession>(() => window.sessionStorage, SESSION);
export const setTabletSession = (s: TabletSession | null): void => write(() => window.sessionStorage, SESSION, s);

/** The Authorization value for this tablet: its key, and the unlock when there is one. */
export function deviceAuthorization(withSession = true): string | null {
  const key = tabletKey();
  if (!key) return null;
  const session = withSession ? tabletSession() : null;
  return session ? `Device ${key.token} ${session.token}` : `Device ${key.token}`;
}

// ----------------------------------------------------------------- step-up

let elevated: { token: string; until: number } | null = null;

/** Act with a full sign-in for the next five minutes. */
export function elevate(accessToken: string, minutes = 5): void {
  elevated = { token: accessToken, until: Date.now() + minutes * 60_000 };
}

export function dropElevation(): void {
  elevated = null;
}

/** The full sign-in to use instead of the tablet, while one is fresh. */
export function elevatedToken(): string | null {
  if (elevated && elevated.until > Date.now()) return elevated.token;
  elevated = null;
  return null;
}

// --------------------------------------------------------------- lock screen

const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

async function call<T>(method: "GET" | "POST", path: string, body?: unknown, withSession = false): Promise<T> {
  const authorization = deviceAuthorization(withSession);
  if (!authorization) throw new Error("This browser is not an enrolled tablet");
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { authorization, ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(text || `${res.status} ${res.statusText}`) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return (await res.json()) as T;
}

export const tabletApi = {
  info: () =>
    call<{ device: { name: string; venueId: string }; orgId: string; orgName: string; venueName: string }>("GET", "/v1/device/info"),
  staff: () => call<{ staff: readonly { userId: string; name: string }[] }>("GET", "/v1/device/staff"),
  unlock: (userId: string, pin: string) =>
    call<{
      session: string;
      user: { userId: string; name: string };
      expiresAt: string;
      idleMinutes: number | null;
      resume: string | null;
    }>("POST", "/v1/device/unlock", { userId, pin }),
  ping: () => call<{ ok: boolean }>("POST", "/v1/device/ping", {}, true),
  lock: (path: string) => call<{ locked: boolean }>("POST", "/v1/device/lock", { path }, true),
};

// ----------------------------------------------------------- the one hook

/** Fired when the server says the tablet locked itself. */
export const LOCKED_EVENT = "baros:tablet-locked";
/** Fired when an action needs a full sign-in; `detail` is which. */
export const STEP_UP_EVENT = "baros:step-up";

let watching = false;

/**
 * Look at every API response once, in tablet mode, and tell the app about
 * the two refusals it must answer: a lock, and a step-up. Installed once,
 * at start-up; a no-op on a browser that is not a tablet.
 */
export function watchTabletResponses(): void {
  if (watching || !tabletKey()) return;
  watching = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await original(input, init);
    // Only a request carrying an unlock: a wrong PIN at the lock screen is
    // a 401 too, and is the lock screen's own business.
    const sentUnlocked = (new Headers(init?.headers).get("authorization") ?? "").trim().split(/\s+/).length === 3;
    if (sentUnlocked && res.status === 401) {
      window.dispatchEvent(new Event(LOCKED_EVENT));
    } else if (res.status === 403) {
      const action = res.headers.get(STEP_UP_HEADER);
      const text = action ? "" : await res.clone().text().catch(() => "");
      if (action || text.startsWith("Full sign-in needed:")) {
        window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, { detail: action ?? "" }));
      }
    }
    return res;
  };
}
